import { buildFacts } from './productFacts.mjs'
import servesData from './serves.json'
import type { ProductFactsData } from 'components/product/ProductFacts'
import type { IProduct, ICategory } from 'store/api'
import type { IProductDetail } from 'components/product/ProductCard/types'

type RecordValue = Record<string, unknown>
const record = (v: unknown): RecordValue =>
  typeof v === 'object' && v !== null ? v as RecordValue : {}
const text = (v: unknown): string => typeof v === 'string' ? v : ''
const positive = (v: unknown): number | null =>
  typeof v === 'number' && Number.isFinite(v) && v > 0 ? v : null
export const parseWeightGrams = (value: unknown): number | null => {
  if (typeof value === 'number') return positive(value)
  if (typeof value !== 'string') return null
  // API weights without a unit are grams; pieces/volumes/compound text are not mass.
  const match = value.trim().toLowerCase().match(/^(\d+(?:[.,]\d+)?)\s*(кг|kg|г|гр|g|gr|грамм(?:а|ов)?|килограмм(?:а|ов)?)?\.?$/)
  if (!match) return null
  const amount = Number(match[1]!.replace(',', '.'))
  return positive(amount * (/^(кг|kg|килограмм)/.test(match[2] || '') ? 1000 : 1))
}
type Input = {
  product: IProduct | null; category: ICategory | null; canonical: string
  productDetail: IProductDetail | undefined
  price: number | undefined; weight: string | undefined; nutrition: unknown
}
export const buildProductRuntime = (input: Input) => {
  const { product, category, canonical, price, weight, nutrition } = input
  if (!product) return { facts: null, label: null, jsonLd: '', title: '', description: '' }
  const grams = parseWeightGrams(weight)
  const baseGrams = parseWeightGrams(product.weight)
  const entry = record(record(servesData)[product.slug])
  const estimateWeight = positive(entry['weight_g']) ?? baseGrams
  // Never reuse a base estimate for an unknown or different variation weight.
  const serves = grams !== null && grams === estimateWeight ? entry : null
  const label = serves ? text(serves['label']) || null : null
  const raw = record(buildFacts({
    product: { ...product, price, weight: grams ?? undefined }, category: category ?? undefined, canonicalUrl: canonical,
    availability: undefined, nutrition, delivery: undefined, serves: serves ?? undefined,
  }))
  const summary: NonNullable<ProductFactsData['summary']> = {}
  const source = record(raw['summary'])
  for (const key of ['composition', 'weight', 'pieces', 'serves', 'perPerson', 'delivery'] as const) {
    const value = source[key]
    if (typeof value === 'string' || (typeof value === 'number' && Number.isFinite(value))) summary[key] = value
  }
  if (!serves) { delete summary.serves; delete summary.perPerson }
  const pairs = raw['faq']
  const faq = Array.isArray(pairs) ? pairs.flatMap((item: unknown) => {
    const pair = record(item), question = text(pair['question']), answer = text(pair['answer'])
    return question && answer ? [{ question, answer }] : []
  }) : []
  const facts: ProductFactsData = { summary, faq }
  const node: RecordValue = {
    '@type': 'Product', '@id': `${canonical}#product`, url: canonical,
    name: product.name, sku: String(product.id),
  }
  // SEO (05.11.2026, Надежда «применяй»): поле каталога приоритетнее шаблона
  // фактов (ТЗ 28.09); шаблон — fallback для товаров с пустым полем В БД.
  const title = text(product.meta?.product_title) || text(raw['title'])
  const description = text(product.meta?.product_description)
    .replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim() || text(raw['description'])
  if (description) node['description'] = description
  if (grams !== null) node['weight'] = { '@type': 'QuantitativeValue', unitCode: 'GRM', value: grams }
  if (positive(price) !== null) node['offers'] = { '@type': 'Offer', price, priceCurrency: 'RUB', url: canonical }
  const graph: RecordValue[] = [node]
  const origin = new URL(canonical).origin
  if (category) graph.push({ '@type': 'BreadcrumbList', itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'Главная', item: origin },
    { '@type': 'ListItem', position: 2, name: category.name, item: `${origin}/categories/${category.slug}` },
    { '@type': 'ListItem', position: 3, name: product.name, item: canonical },
  ] })
  if (faq.length) graph.push({ '@type': 'FAQPage', mainEntity: faq.map(({ question, answer }) => ({
    '@type': 'Question', name: question, acceptedAnswer: { '@type': 'Answer', text: answer },
  })) })
  return { facts, label, title, description, jsonLd: JSON.stringify({ '@context': 'https://schema.org', '@graph': graph }).replace(/</g, '\\u003c') }
}

// Use the existing public graph at the single View owner; facts/metadata keep their original API.
export const buildProductGraphRuntime = (input: Input, buildGraph: typeof import('./jsonLd').buildProductGraph) => {
  const runtime = buildProductRuntime(input)
  const { product, category, canonical } = input
  if (!product || !category) return runtime
  const parts = new URL(canonical).pathname.split('/').filter(Boolean)
  if (parts.length !== 3 || parts[0] !== 'product' || decodeURIComponent(parts[2]!) !== product.slug)
    throw new Error('Product canonical does not match current product')
  const grams = parseWeightGrams(input.weight), nutrition = record(input.nutrition)
  const nutrient = (value: unknown): string => {
    const raw = typeof value === 'number' ? String(value) : typeof value === 'string' ? value.trim() : ''
    return /^\d+(?:[.,]\d+)?$/.test(raw) && positive(Number(raw.replace(',', '.'))) !== null
      ? String(Number(raw.replace(',', '.'))) : ''
  }
  const selected = {
    kcal: nutrient(nutrition['kcal']), proteins: nutrient(nutrition['proteins']),
    fats: nutrient(nutrition['fats']), hydrocarbonates: nutrient(nutrition['hydrocarbonates']),
  }
  const legacy = buildGraph({
    product: { ...product, price: positive(input.price) ?? 0, weight: grams === null ? '' : String(grams) },
    category, canonicalCategory: { ...category, slug: decodeURIComponent(parts[1]!) },
    detail: input.productDetail ? { ...input.productDetail,
      product: { ...input.productDetail.product, ...selected, weight: grams ?? 0 },
    } : undefined,
  })
  const menu = legacy['@graph'].find(n => n['@type'] === 'Menu') as
    { hasMenuSection: { hasMenuItem: RecordValue[] }[] } | undefined
  const item = menu?.hasMenuSection[0]?.hasMenuItem[0]
  if (!item) throw new Error('Existing product graph contract changed')
  item['url'] = canonical; item['@id'] = `${canonical}#menuitem`
  // Unknown variant nutrition never inherits base detail values; unknown mass has no serving size.
  if (grams === null && item['nutrition']) delete record(item['nutrition'])['servingSize']
  const document = JSON.parse(runtime.jsonLd), nodes = document['@graph'] as RecordValue[]
  const node = nodes.find(n => n['@type'] === 'Product')
  if (!node) throw new Error('Product facts graph contract changed')
  if (item['image']) node['image'] = item['image']
  if (item['offers']) {
    const offer = record(item['offers']); offer['@id'] = `${canonical}#offer`; offer['url'] = canonical
    node['offers'] = offer; item['offers'] = { '@id': offer['@id'] }
  }
  return { ...runtime, jsonLd: JSON.stringify({ ...document,
    '@graph': [...nodes, ...legacy['@graph']],
  }).replace(/</g, '\\u003c') }
}
